import { useState, useEffect } from "react";

// Recharts tải lười: ~40-50% bundle chỉ phục vụ 1 chart ở tab Daily,
// không bắt phụ huynh chờ nó mới thấy trang. Webpack tự tách chunk riêng.
let _rechartsPromise = null;
const loadRecharts = () => (_rechartsPromise ||= import("recharts"));

// ============================================================
// 👇 THAY API URL KHI DEPLOY THẬT
// ============================================================
const API_URL = "https://script.google.com/macros/s/AKfycbxhDl4CWkAbPFCw6xgKmVADRzyADDCnG1EeRJNRxyVOz4SDbyG_qfCbk2rfJHLLQH7L/exec";
const FETCH_TIMEOUT_MS = 25000;
const CLIENT_CACHE_TTL_MS = 5 * 60 * 1000;


// ============================================================
// DESIGN TOKENS
// ============================================================
const C = {
  // ── Design tokens (British-Council-inspired: clean, academic, low-color) ──
  brand:"#BF1E2E", brandDark:"#991B1B", brandSoft:"#FEF2F2",
  text:"#111827", textSecondary:"#6B7280", textMuted:"#9CA3AF",
  bg:"#F8FAFC", surface:"#FFFFFF", surfaceSoft:"#F1F5F9",
  border:"#E5E7EB", borderSoft:"#EEF2F7",
  success:"#16A34A", successSoft:"#DCFCE7",
  warning:"#CA8A04", warningSoft:"#FEF9C3",
  error:"#DC2626", errorSoft:"#FEE2E2",
  info:"#22384F", infoSoft:"#EFF6FF",
  ink:"#22384F", inkSoft:"rgba(34,56,79,0.72)",
  purple:"#7C3AED", purpleSoft:"#F5F3FF",
  white:"#FFFFFF",
  // ── Aliases (tên cũ → token mới) để không vỡ code hiện có ──
  red:"#BF1E2E", redLight:"#BF1E2E", redBg:"#FEF2F2",
  gray900:"#111827", gray700:"#6B7280", gray500:"#6B7280",
  gray400:"#9CA3AF", gray300:"#CBD5E1", gray200:"#E5E7EB",
  gray100:"#EEF2F7", gray50:"#F8FAFC",
  green:"#16A34A", greenBg:"#DCFCE7", greenBorder:"#BBF7D0",
  yellow:"#CA8A04", yellowBg:"#FEF9C3", yellowBorder:"#FDE68A",
  redDot:"#DC2626", redDotBg:"#FEE2E2", redDotBorder:"#FCA5A5",
  blue:"#22384F", blueBg:"#EFF6FF", purpleBg:"#F5F3FF",
  orange:"#CA8A04",
};
// SK: chỉ dành cho dot marker nhỏ trong chart/legend nếu cần sau này.
// KHÔNG dùng tô màu chữ tên skill — skill là danh mục, không phải trạng thái.
const SK = { speaking:"#BF1E2E", listening:"#22384F", reading:"#16A34A", writing:"#7C3AED" };
const CARD_STYLE = {
  background:"#FFFFFF", borderRadius:18, padding:"16px 14px", marginBottom:12,
  border:"1px solid #EEF2F7", boxShadow:"0 1px 3px rgba(15,23,42,0.04)",
};
const SI = { speaking:"🗣️", listening:"👂", reading:"📖", writing:"✍️" };
const SV = { speaking:"Speaking", listening:"Listening", reading:"Reading", writing:"Writing" };
const SKILL_KEYS = ["speaking","listening","reading","writing"];

// Frosted-glass style cho các panel nhỏ bên trong card (tint = gradient trong mờ).
function glassPanel(tint, border = "rgba(255,255,255,0.6)") {
  return {
    background: tint,
    border: `1px solid ${border}`,
    boxShadow: "0 4px 14px rgba(15,23,42,0.05), inset 0 1px 0 rgba(255,255,255,0.6)",
    backdropFilter: "blur(14px) saturate(170%)",
    WebkitBackdropFilter: "blur(14px) saturate(170%)",
  };
}

// Mô tả CEFR ngắn gọn bằng tiếng Việt (dẫn theo token đầu, vd "A2 / approaching B1" -> A2)
const CEFR_VI = {
  "Pre-A1":"Khởi đầu", "A1":"Sơ cấp", "A1+":"Sơ cấp khá",
  "A2":"Cơ bản", "B1":"Trung cấp", "B2":"Trung cao cấp", "C1":"Cao cấp",
};
function cefrDesc(cefr) {
  if (!cefr) return "";
  const key = String(cefr).trim();
  if (CEFR_VI[key]) return CEFR_VI[key];
  const tok = key.split(/[\s/]+/)[0];
  return CEFR_VI[tok] || "";
}

function hasWeeklyContent(sk) {
  return SKILL_KEYS.some(key =>
    sk?.[`${key}Comment`] || sk?.[`${key}CommentVi`]
  ) || !!(sk?.attitudeComment || sk?.attitudeCommentVi);
}

// Normalize a track value so "ESL ", "esl" etc. compare equal.
function normTrack(v) {
  return String(v || "").trim().toUpperCase();
}

// ── Exam-based helpers ──
// Lấy điểm 1 kỹ năng từ một bài thi: IELTS -> .band, ESL -> .score. null nếu không có.
function examSkillValue(exam, skill) {
  const s = exam && exam[skill];
  if (!s) return null;
  return s.band ?? s.score ?? null;
}
// ── Overall value of an exam (works for both ESL /10 and IELTS band) ──
function examOverallValue(exam) {
  if (!exam) return null;
  return exam.avg ?? exam.overall ?? exam.overallScore ?? exam.overall_score ?? null;
}
// True only when an exam object actually carries results (guards against {} from GAS).
function hasExamData(exam) {
  if (!exam) return false;
  return (
    exam.avg != null || exam.overall != null || !!exam.cefr ||
    SKILL_KEYS.some(sk => examSkillValue(exam, sk) != null)
  );
}
// Chỉ so sánh tiến bộ khi hai bài thi cùng track/thang điểm.
// ESL = điểm /10, IELTS = band 0-9; không trộn hai hệ để tránh delta sai.
function sameExamTrack(a, b) {
  return hasExamData(a) && hasExamData(b) && normTrack(a?.track) === normTrack(b?.track);
}
// Latest available exam, in priority Final > Midterm > Placement.
function getLatestExam(exams) {
  if (hasExamData(exams?.final))     return { key:"final",     label:"Final Assessment", exam:exams.final };
  if (hasExamData(exams?.midterm))   return { key:"midterm",   label:"Midterm Check",    exam:exams.midterm };
  if (hasExamData(exams?.placement)) return { key:"placement", label:"Placement Test",   exam:exams.placement };
  return null;
}
// Featured "current assessment" card = latest real exam (never placement).
// Independent of the selected comment week.
function getDisplayExam(exams) {
  if (hasExamData(exams?.final))   return { key:"final",   label:"Final Assessment", subtitle:"Final learning outcome", color:C.purple,  exam:exams.final };
  if (hasExamData(exams?.midterm)) return { key:"midterm", label:"Midterm Check",    subtitle:"Progress checkpoint",   color:C.success, exam:exams.midterm };
  return null;
}
function formatScoreValue(v, isIELTS) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return isIELTS ? `Band ${Number(v).toFixed(1)}` : `${Number(v).toFixed(1)}/10`;
}
function formatDelta(delta) {
  if (delta == null || Number.isNaN(Number(delta))) return "—";
  const n = Number(delta);
  return `${n > 0 ? "+" : ""}${n.toFixed(1)}`;
}
function getDeltaColor(delta) {
  if (delta == null) return C.textMuted;
  if (delta > 0) return C.success;
  if (delta < 0) return C.error;
  return C.textMuted;
}
// Skill-by-skill comparison rows across Placement/Midterm/Final.
// Cambridge YLE levels are the only ones that use shields.
const YLE_LEVELS = ["Starters", "Movers", "Flyers"];
function isYleExam(exam) {
  return YLE_LEVELS.includes(String(exam?.examLevel || "").trim());
}
// A shield value is only displayable if it carries a real count (e.g. "12/15"),
// not a level name like "Flyers".
function isShieldCount(shield) {
  return shield != null && /\d/.test(String(shield));
}

function getSkillDeltaRows(exams, placementSameTrack) {
  const placement = placementSameTrack ? exams?.placement : null;
  const midterm   = exams?.midterm || null;
  const finalExam = exams?.final   || null;
  const rows = SKILL_KEYS.map(sk => {
    const p = placement ? examSkillValue(placement, sk) : null;
    const m = midterm   ? examSkillValue(midterm, sk)   : null;
    const f = finalExam ? examSkillValue(finalExam, sk) : null;
    const values   = [p, m, f].filter(v => v != null);
    const latest   = f ?? m ?? p;
    const baseline = p ?? m ?? null;
    // Only show a change when there are at least two real assessment points.
    const change = (values.length >= 2 && latest != null && baseline != null)
      ? latest - baseline
      : null;
    return { key:sk, label:SV[sk], placement:p, midterm:m, final:f, latest, change };
  });
  return rows.filter(r => r.placement != null || r.midterm != null || r.final != null);
}
// Cross-track (e.g. IELTS placement, ESL study): compare only same-track exams
// (Midterm/Final), never the off-track placement.
function getCrossTrackSkillRows(exams) {
  const midterm   = exams?.midterm || null;
  const finalExam = exams?.final   || null;
  const canCompare = sameExamTrack(midterm, finalExam);
  return SKILL_KEYS.map(sk => {
    const m = midterm   ? examSkillValue(midterm, sk)   : null;
    const f = finalExam ? examSkillValue(finalExam, sk) : null;
    const change = (canCompare && m != null && f != null) ? f - m : null;
    return { key:sk, label:SV[sk], midterm:m, final:f, change };
  }).filter(r => r.midterm != null || r.final != null);
}
// All skills sharing the highest positive growth (handles ties). null if none grew.
function getStrongestGrowthGroup(rows) {
  const valid = rows.filter(r => r.change != null && r.change > 0);
  if (!valid.length) return null;
  const maxChange = Math.max(...valid.map(r => r.change));
  const skills = valid.filter(r => r.change === maxChange);
  return { change: maxChange, skills, labels: skills.map(s => s.label) };
}
function getWeakestSkillFromRows(rows) {
  const valid = rows.filter(r => r.latest != null);
  if (!valid.length) return null;
  return valid.reduce((min, r) => r.latest < min.latest ? r : min, valid[0]);
}
function buildProgressSummary({ exams, latestExamObj, skillDeltaRows, placementSameTrack }) {
  const latest = latestExamObj?.exam || null;
  // Same-track: baseline = placement. Cross-track: baseline = midterm (only if final exists too).
  let baseline = null;
  if (placementSameTrack) baseline = exams?.placement || null;
  else if (hasExamData(exams?.midterm) && hasExamData(exams?.final)) baseline = exams.midterm;
  const latestOverall   = examOverallValue(latest);
  const baselineOverall = baseline ? examOverallValue(baseline) : null;
  const overallDelta = (latestOverall != null && baselineOverall != null && latest !== baseline)
    ? latestOverall - baselineOverall : null;
  let status = "Not enough data yet";
  if (overallDelta != null) {
    if (overallDelta > 0.3) status = "Improving";
    else if (overallDelta < -0.3) status = "Needs review";
    else status = "Stable";
  } else if (latestExamObj?.key === "placement") {
    status = "Baseline established";
  } else if (latestExamObj?.key === "midterm") {
    status = "Current profile established";
  }
  return {
    status, latestOverall, baselineOverall, overallDelta,
    strongestGrowth: getStrongestGrowthGroup(skillDeltaRows),
    weakestSkill: getWeakestSkillFromRows(skillDeltaRows),
  };
}

// Chuẩn hóa weekRange từ backend ("01/06-06/06", "01/06–06/06"...) về "1/6 – 6/6".
// Không parse được thì trả nguyên văn — an toàn với mọi format sheet có thể trả về.
function formatWeekRange(raw) {
  if (!raw) return raw;
  const m = String(raw).match(/^\s*(\d{1,2})\/(\d{1,2})\s*[-–—]\s*(\d{1,2})\/(\d{1,2})\s*$/);
  if (!m) return raw;
  const [, d1, m1, d2, m2 ] = m.map(Number);
  return `${d1}/${m1} – ${d2}/${m2}`;
}

function isVisibleWeek(sk) {
  return Number.isInteger(sk?.weekNum) && sk.weekNum >= 1 && sk.weekNum <= 4 && hasWeeklyContent(sk);
}

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[parts.length - 2].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// ============================================================
// SKELETON LOADING SCREEN
// ============================================================
function SkeletonBox({ w = "100%", h = 14, radius = 8, style = {} }) {
  return (
    <div style={{ width: w, height: h, borderRadius: radius, background: `linear-gradient(90deg, ${C.gray100} 25%, ${C.gray200} 50%, ${C.gray100} 75%)`, backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite", ...style }} />
  );
}

function SkeletonScreen() {
  return (
    <div style={{ fontFamily:"'Be Vietnam Pro',sans-serif", background:C.bg, minHeight:"100vh", maxWidth: 480, margin:"0 auto", paddingBottom: 76 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Be+Vietnam+Pro:wght@400;600;700;800;900&display=swap');
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes spin { to{transform:rotate(360deg)} }
      `}</style>

      {/* Header skeleton */}
      <div style={{ background:"linear-gradient(170deg, #D8606B 0%, #C73D4B 55%, #AD2837 100%)", padding:"24px 16px 18px", margin:"10px 12px 0", borderRadius:30, display:"flex", flexDirection:"column", alignItems:"center" }}>
        <div style={{ width:110, height:10, borderRadius:6, background:"rgba(255,255,255,0.28)" }} />
        <div style={{ width:"55%", height:18, borderRadius:8, background:"rgba(255,255,255,0.34)", marginTop:12 }} />
        <div style={{ display:"flex", width:"100%", marginTop:20, gap:8 }}>
          {[1,2,3].map(i => <div key={i} style={{ flex:1, height:26, borderRadius:8, background:"rgba(255,255,255,0.22)" }} />)}
        </div>
      </div>

      {/* Content skeleton */}
      <div style={{ padding:"12px 12px 0" }}>
        {[1,2].map(i => (
          <div key={i} style={{ background:C.white, borderRadius:16, padding:"16px 14px", marginBottom:12, border:`1px solid ${C.gray200}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
              <SkeletonBox w={22} h={22} radius={6} />
              <SkeletonBox w="45%" h={14} />
            </div>
            <SkeletonBox h={12} style={{ marginBottom:8 }} />
            <SkeletonBox w="80%" h={12} style={{ marginBottom:8 }} />
            <SkeletonBox w="60%" h={12} />
          </div>
        ))}
        <div style={{ background:C.white, borderRadius:16, padding:"16px 14px", marginBottom:12, border:`1px solid ${C.gray200}` }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
            <SkeletonBox w={22} h={22} radius={6} />
            <SkeletonBox w="35%" h={14} />
          </div>
          <SkeletonBox h={140} radius={10} />
        </div>
      </div>

      {/* Bottom nav skeleton */}
      <div style={{ position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)", width:"100%", maxWidth:480, background:"rgba(255,255,255,0.96)", borderTop:`1px solid ${C.gray200}`, display:"flex", padding:"10px 6px 14px", gap:6 }}>
        {[1,2,3,4].map(i => (
          <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:5 }}>
            <SkeletonBox w={22} h={22} radius={6} />
            <SkeletonBox w={36} h={9} radius={4} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// ERROR SCREEN
// ============================================================
function ErrorScreen({ message }) {
  return (
    <div style={{ fontFamily:"'Be Vietnam Pro',sans-serif", minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", background:C.gray50, gap:14, padding:28 }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Be+Vietnam+Pro:wght@400;600;700;800;900&display=swap');`}</style>
      <div style={{ fontSize:13, fontWeight:800, letterSpacing:3, color:C.red, textTransform:"uppercase" }}>SPARTA CAMP 2026</div>
      <div style={{ marginTop:4, padding:"16px 20px", background:C.white, borderRadius:12, border:`1px solid ${C.gray200}`, textAlign:"center" }}>
        <div style={{ fontSize:13, color:C.gray500, marginBottom:12 }}>Vui lòng liên hệ trung tâm để được hỗ trợ.</div>
        <a href="tel:08684122555" style={{ fontSize:13, fontWeight:700, color:C.red, textDecoration:"none", display:"block" }}>📞 0868 41 2255</a>
        <a href="mailto:info.spartacamp@gmail.com" style={{ fontSize:12, color:C.gray500, textDecoration:"none", display:"block", marginTop:4 }}>✉️ info.spartacamp@gmail.com</a>
      </div>
    </div>
  );
}

// ============================================================
// COMPONENTS
// ============================================================
function StatusDot({ status, size = 26 }) {
  const cfg = {
    present:{ bg:C.successSoft, color:C.success },
    late:   { bg:C.warningSoft, color:C.warning },
    half:   { bg:C.infoSoft,    color:C.info    },
    absent: { bg:C.errorSoft,   color:C.error   },
  }[status];
  if (!cfg) return null;
  const ic = Math.round(size * 0.56);
  const common = { width:ic, height:ic, viewBox:"0 0 24 24", fill:"none", stroke:"currentColor", strokeWidth:2.4, strokeLinecap:"round", strokeLinejoin:"round" };
  const icon =
    status === "present" ? <svg {...common}><path d="M5 12.5l4.5 4.5L19 7" /></svg> :
    status === "late"    ? <svg {...common}><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></svg> :
    status === "half"    ? <svg {...common}><circle cx="12" cy="12" r="8.5" /><path d="M12 3.5a8.5 8.5 0 0 1 0 17z" fill="currentColor" stroke="none" /></svg> :
                           <svg {...common}><path d="M7.5 7.5l9 9M16.5 7.5l-9 9" /></svg>;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width:size, height:size, borderRadius:"50%", background:cfg.bg, color:cfg.color, flexShrink:0 }}>
      {icon}
    </span>
  );
}

function Section({ icon, title, titleEn, children, style={}, headerRight }) {
  return (
    <div style={{ ...CARD_STYLE, ...style }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
        {icon && <span style={{ display:"flex", alignItems:"center", color:C.ink }}><NavIcon name={icon} size={18} /></span>}
        <div style={{ flex:1 }}>
          <div style={{ fontSize:14, fontWeight:800, color:C.text }}>{title}</div>
          {titleEn && <div style={{ fontSize:10.5, color:C.textMuted, marginTop:2 }}>{titleEn}</div>}
        </div>
        {headerRight && <div style={{ flexShrink:0 }}>{headerRight}</div>}
      </div>
      {children}
    </div>
  );
}



// ── Vocab chart (Recharts nạp lười — RC là module recharts, null khi đang tải) ──
function VocabChart({ RC, vocab }) {
  if (!RC) {
    return <div style={{ height:165, borderRadius:14, background:C.surfaceSoft }} />;
  }
  const { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } = RC;
  return (
    <ResponsiveContainer width="100%" height={165}>
      <BarChart data={vocab} accessibilityLayer={false} margin={{ top:5, right:5, bottom:5, left:-22 }}>
        <XAxis dataKey="date" tick={{ fontSize:8, fill:C.gray400 }} axisLine={{ stroke:C.border }} tickLine={false} />
        <YAxis domain={[0,100]} ticks={[0,50,100]} tick={{ fontSize:8, fill:C.gray400 }} axisLine={{ stroke:C.border }} tickLine={false} />
        <Tooltip cursor={false} content={({ active, payload }) => {
          if (!active || !payload || !payload.length) return null;
          const e = payload[0].payload;
          return (
            <div style={{ background:"linear-gradient(135deg, rgba(255,255,255,0.78), rgba(255,255,255,0.42))", backdropFilter:"blur(18px) saturate(185%)", WebkitBackdropFilter:"blur(18px) saturate(185%)", borderRadius:12, boxShadow:"0 8px 24px rgba(15,23,42,0.16), inset 0 1px 0 rgba(255,255,255,0.9)", padding:"8px 14px", textAlign:"center" }}>
              {e?.status === "absent" ? (
                <div style={{ fontSize:11, fontWeight:700, color:C.textSecondary }}>Absent</div>
              ) : (
                <>
                  <div style={{ fontSize:13, fontWeight:800, color:C.ink, lineHeight:1.2 }}>{e?.display}</div>
                  <div style={{ fontSize:11, fontWeight:700, color:C.textSecondary, marginTop:2 }}>{e?.percent}%</div>
                </>
              )}
            </div>
          );
        }} />
        <Bar dataKey="percent" radius={[8,8,0,0]} barSize={16}
          background={{ fill:"rgba(226,232,240,0.22)", radius:[8,8,0,0] }}>
          {vocab.map((e,i) => <Cell key={i} fill={e.status==="absent"?"transparent":e.percent>=80?"#4ADE80":e.percent>=50?"#FCD34D":"#F87171"} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Bilingual Comment block ───────────────────────────────────
function CommentBlock({ label, icon, comment, commentVi, showVi }) {
  const text = showVi ? (commentVi || comment) : (comment || commentVi);
  if (!text) return null;
  return (
    <div style={{ padding:"11px 12px", marginBottom:8, borderRadius:12, background:C.surfaceSoft, border:`1px solid ${C.borderSoft}` }}>
      <div style={{ fontSize:10.5, fontWeight:700, color:C.textSecondary, marginBottom:5, textTransform:"uppercase", letterSpacing:0.3 }}>{icon ? `${icon} ` : ""}{label}</div>
      <div style={{ fontSize:12.5, color:C.text, lineHeight:1.6 }}>{text}</div>
    </div>
  );
}

// ── DayCell: ô lịch hiện đại — số ngày nằm trong vòng trạng thái ──
// present: đặc xanh / late: đặc hổ phách / half: viền xanh / absent: số trần + chấm đỏ
function DayCell({ day, size = 30 }) {
  const dayNum = String(day.date).split("/")[0];
  const base = {
    width:size, height:size, borderRadius:"50%",
    display:"inline-flex", alignItems:"center", justifyContent:"center",
    fontSize:12, fontWeight:700, boxSizing:"border-box",
  };
  const byStatus = {
    present:{ ...base, background:C.success, color:C.white },
    late:   { ...base, background:"#F59E0B", color:C.white },
    half:   { ...base, background:C.white, border:`2px solid ${C.success}`, color:C.success },
    absent: { ...base, background:"transparent", color:C.textSecondary },
  };
  const style = byStatus[day.status] || { ...base, background:"transparent", color:C.textMuted };
  return (
    <span style={{ position:"relative", display:"inline-flex", flexDirection:"column", alignItems:"center" }}>
      <span style={style}>{dayNum}</span>
      {/* Chấm đỏ dot-marker: ngày nghỉ vẫn phải nhìn thấy được */}
      <span style={{ width:4, height:4, borderRadius:"50%", marginTop:2, background:day.status === "absent" ? C.error : "transparent" }} />
    </span>
  );
}

function CalendarGrid({ attendance }) {
  const dh = ["Mon","Tue","Wed","Thu","Fri","Sat"];
  const weeks = [];
  for (let i = 0; i < attendance.length; i += 6)
    weeks.push({ label:`W${weeks.length+1}`, days:attendance.slice(i,i+6) });
  const counts = {
    present: attendance.filter(a => a.status === "present").length,
    late:    attendance.filter(a => a.status === "late").length,
    half:    attendance.filter(a => a.status === "half").length,
    absent:  attendance.filter(a => a.status === "absent").length,
  };
  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"30px repeat(6,1fr)", gap:3, marginBottom:6 }}>
        <div />
        {dh.map((d,i) => <div key={i} style={{ textAlign:"center", fontSize:10, fontWeight:700, color:C.gray500 }}>{d}</div>)}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} style={{ display:"grid", gridTemplateColumns:"30px repeat(6,1fr)", gap:3, marginBottom:5, paddingBottom:5, borderBottom:wi<weeks.length-1?`1px solid ${C.gray100}`:"none" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{ background:C.gray100, padding:"2px 4px", borderRadius:4, fontSize:8, fontWeight:700, color:C.gray500 }}>{week.label}</span>
          </div>
          {week.days.map((day,di) => (
            <div key={di} style={{ textAlign:"center" }}>
              <DayCell day={day} />
            </div>
          ))}
        </div>
      ))}
      <div style={{ display:"flex", justifyContent:"center", flexWrap:"wrap", gap:12, marginTop:8, paddingTop:8, borderTop:`1px solid ${C.gray100}` }}>
        {[
          { key:"present", label:"Present",  color:C.green,  sample:{ background:C.success } },
          { key:"late",    label:"Late",     color:C.yellow, sample:{ background:"#F59E0B" } },
          { key:"half",    label:"Half day", color:C.green,  sample:{ background:C.white, border:`2px solid ${C.success}` } },
          { key:"absent",  label:"Absent",   color:C.redDot, sample:{ background:C.error } },
        ].map((l,i)=>(
          <div key={i} style={{ display:"flex", alignItems:"center", gap:4 }}>
            <span style={{ width:l.key==="absent"?6:13, height:l.key==="absent"?6:13, borderRadius:"50%", boxSizing:"border-box", flexShrink:0, ...l.sample }} />
            <span style={{ fontSize:10, color:C.gray500, fontWeight:600 }}>{l.label}</span>
            <span style={{ fontSize:10, color:l.color, fontWeight:800 }}>{counts[l.key]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RawPopup({ data, skill, isIELTS, combinedRW, onClose }) {
  const scoreVal = data.score ?? data.band;
  const displayVal = isIELTS ? `Band ${scoreVal?.toFixed(1)}` : `${scoreVal?.toFixed(1)}/10`;
  const hasRaw = data.raw != null && data.max != null;
  const title = (combinedRW && skill === "reading") ? "Reading & Writing" : SV[skill];
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.32)", backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:2000 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:"linear-gradient(135deg, rgba(255,255,255,0.88), rgba(255,255,255,0.68))", backdropFilter:"blur(24px) saturate(185%)", WebkitBackdropFilter:"blur(24px) saturate(185%)", boxShadow:"0 18px 44px rgba(15,23,42,0.22), inset 0 1px 0 rgba(255,255,255,0.95)", borderRadius:20, padding:"22px", width:260 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
          <span style={{ display:"flex", alignItems:"center", color:C.ink }}><NavIcon name={skill} size={20} /></span>
          <div style={{ fontSize:14, fontWeight:800, color:C.gray900 }}>{title}</div>
          <button onClick={onClose} style={{ marginLeft:"auto", background:"rgba(226,232,240,0.55)", border:"none", borderRadius:"50%", width:28, height:28, cursor:"pointer", fontSize:13, color:C.gray500 }}>✕</button>
        </div>
        <div style={{ background:"rgba(241,245,249,0.55)", borderRadius:12, padding:"16px", border:"1px solid rgba(255,255,255,0.6)", textAlign:"center" }}>
          <div style={{ fontSize:32, fontWeight:900, color:C.ink, lineHeight:1 }}>{displayVal}</div>
          {hasRaw && (
            <div style={{ marginTop:10 }}>
              <span style={{ display:"inline-block", padding:"3px 14px", borderRadius:20, background:"rgba(226,232,240,0.55)", fontSize:12, fontWeight:700, color:"#374151" }}>
                Raw score: {data.raw}/{data.max}
              </span>
            </div>
          )}
          {isIELTS && skill === "writing" && (data.task1 != null || data.task2 != null) && (
            <div style={{ marginTop:10, display:"flex", gap:6, justifyContent:"center" }}>
              {data.task1 != null && (
                <span style={{ display:"inline-block", padding:"3px 12px", borderRadius:20, background:"rgba(226,232,240,0.55)", fontSize:11, fontWeight:700, color:"#374151" }}>
                  Task 1: {data.task1}
                </span>
              )}
              {data.task2 != null && (
                <span style={{ display:"inline-block", padding:"3px 12px", borderRadius:20, background:"rgba(226,232,240,0.55)", fontSize:11, fontWeight:700, color:"#374151" }}>
                  Task 2: {data.task2}
                </span>
              )}
            </div>
          )}
        </div>
        <div style={{ textAlign:"center", marginTop:10, fontSize:10, color:C.gray400 }}>Tap ngoài để đóng</div>
      </div>
    </div>
  );
}

// Icon (i) tròn nhỏ, bấm mở popup thông tin
function InfoDot({ onClick, color }) {
  const c = color || C.gray400;
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick && onClick(); }} aria-label="Thông tin đánh giá"
      style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width:16, height:16,
        background:"transparent", border:"none", cursor:"pointer", padding:0, marginLeft:6,
        verticalAlign:"middle", flexShrink:0, lineHeight:0 }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4" />
        <path d="M12 8h.01" />
      </svg>
    </button>
  );
}

// Popup "Thông tin đánh giá" — gom các dòng chú thích vào một chỗ
function InfoModal({ paragraphs, onClose }) {
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.32)", backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:2000, padding:20 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:"linear-gradient(135deg, rgba(255,255,255,0.88), rgba(255,255,255,0.68))", backdropFilter:"blur(24px) saturate(185%)", WebkitBackdropFilter:"blur(24px) saturate(185%)", boxShadow:"0 18px 44px rgba(15,23,42,0.22), inset 0 1px 0 rgba(255,255,255,0.95)", borderRadius:18, padding:"20px", maxWidth:320, width:"100%" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
          <div style={{ fontSize:15, fontWeight:800, color:C.gray900 }}>Thông tin đánh giá</div>
          <button onClick={onClose} aria-label="Đóng" style={{ border:"none", background:"transparent", fontSize:22, color:C.gray400, cursor:"pointer", lineHeight:1, padding:0 }}>×</button>
        </div>
        {paragraphs.map((p, i) => (
          <p key={i} style={{ fontSize:12, lineHeight:1.6, color:C.gray700, margin:"0 0 10px" }}>{p}</p>
        ))}
        <button onClick={onClose} style={{ width:"100%", marginTop:4, padding:"10px", borderRadius:10, border:"none", background:C.red, color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer" }}>Đóng</button>
      </div>
    </div>
  );
}

function ExamCard({ exam, label, icon, onInfo }) {
  const [popup, setPopup] = useState(null);
  if (!exam) return null;
  const isIELTS = exam.track === "IELTS";
  const isPlacement = /placement/i.test(String(label));
  const combinedRW = !!exam.combinedRW;   // Flyers: gộp Reading & Writing
  const skills = combinedRW
    ? ["speaking","listening","reading"]
    : ["speaking","listening","reading","writing"];
  const skillLabel = (sk) => (combinedRW && sk === "reading") ? "Reading & Writing" : SV[sk];
  // Panel kết quả: ESL (CEFR/Grade/Shield) hoặc IELTS (Estimated Band + CEFR)
  const displayableShield = !isPlacement && isYleExam(exam) && isShieldCount(exam.shield);
  const eslPanel = !isIELTS && (exam.cefr || (!isPlacement && (exam.grade || displayableShield)));
  const ieltsPanel = isIELTS && exam.avg != null;
  // Một kiểu panel neutral duy nhất cho mọi loại bài thi — loại bài thể hiện bằng chip chữ, không bằng màu.
  const neutralGlass = glassPanel("linear-gradient(135deg, rgba(226,232,240,0.6), rgba(248,250,252,0.32))");
  return (
    <div style={{ marginBottom:12 }}>
      {/* Điểm hiện trong panel; chỉ fallback ra góc phải khi không có panel nào */}
      {exam.avg != null && !eslPanel && !ieltsPanel && (
        <div style={{ textAlign:"right", margin:"-2px 4px 2px" }}>
          <span style={{ fontSize:14, fontWeight:900, color:C.ink }}>{exam.avg.toFixed(1)}</span>
          <span style={{ fontSize:9, fontWeight:600, color:C.gray400 }}> /10</span>
        </div>
      )}
      {ieltsPanel && (
        <div style={{ ...neutralGlass, borderRadius:16, padding:"13px 15px", marginBottom:12 }}>
          <div style={{ display:"flex", alignItems:"flex-start" }}>
            <div>
              <div style={{ fontSize:9, color:C.gray500, fontWeight:700, marginBottom:3, letterSpacing:.3, display:"flex", alignItems:"center" }}>
                BAND IELTS THAM KHẢO {onInfo && <InfoDot onClick={onInfo} />}
              </div>
              <div style={{ fontSize:24, fontWeight:900, color:C.ink, lineHeight:1 }}>{exam.avg.toFixed(1)}</div>
            </div>
          </div>
        </div>
      )}
      {eslPanel && (
        <div style={{ ...neutralGlass, borderRadius:16, padding:"13px 15px", marginBottom:12 }}>
          {/* Hàng 2: hai stat song song — số đi với số */}
          <div style={{ display:"flex", alignItems:"flex-end", gap:10 }}>
            {exam.cefr && (
              <div>
                <div style={{ fontSize:22, fontWeight:900, color:C.ink, lineHeight:1 }}>{exam.cefr}</div>
                <div style={{ fontSize:9, color:C.gray500, fontWeight:600, marginTop:4, display:"flex", alignItems:"center" }}>
                  {isPlacement
                    ? "Trình độ ước tính"
                    : `Khung CEFR${cefrDesc(exam.cefr) ? ` · ${cefrDesc(exam.cefr)}` : ""}`}
                  {onInfo && <InfoDot onClick={onInfo} />}
                </div>
              </div>
            )}
            <div style={{ flex:1 }} />
            {exam.avg != null && (
              <div style={{ textAlign:"right" }}>
                <div style={{ lineHeight:1 }}>
                  <span style={{ fontSize:22, fontWeight:900, color:C.ink }}>{exam.avg.toFixed(1)}</span>
                  <span style={{ fontSize:10, fontWeight:600, color:C.gray400 }}> /10</span>
                </div>
                <div style={{ fontSize:9, color:C.gray500, fontWeight:600, marginTop:4 }}>Avg</div>
              </div>
            )}
          </div>
          {/* Hàng 3: chip kết quả — chip đi với chip, nằm ngang */}
          {(!isPlacement && (exam.grade || displayableShield)) && (
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:10 }}>
              {/* Grade = kết quả đậu/xếp loại → được dùng màu ngữ nghĩa */}
              {exam.grade && (
                <span style={{ padding:"3px 12px", borderRadius:20, background:C.greenBg, border:`1px solid ${C.greenBorder}`, fontSize:11, fontWeight:700, color:C.green }}>
                  {exam.grade}
                </span>
              )}
              {/* Shield = chỉ số kết quả, không phải tốt/xấu → neutral */}
              {displayableShield && (
                <span style={{ padding:"3px 12px", borderRadius:20, background:C.surfaceSoft, border:`1px solid ${C.border}`, fontSize:11, fontWeight:700, color:C.ink }}>
                  🛡️ {exam.shield}
                </span>
              )}
            </div>
          )}
          {exam.cambridgeScale != null && (
            <div style={{ fontSize:9, color:C.gray400, marginTop:9 }}>
              ≈ {exam.cambridgeScale} thang Cambridge (ước tính){exam.examLevel ? ` · ${exam.examLevel}` : ""}
            </div>
          )}
        </div>
      )}
      {/* skill row — Flyers: 3 ô (R&W gộp), KET/PET & IELTS: 4 ô. Tên skill neutral, không tô màu. */}
      <div style={{ display:"grid", gridTemplateColumns:`repeat(${skills.length},1fr)` }}>
        {skills.map((sk, i) => {
          const d = exam[sk];
          if (!d) return null;
          const scoreVal = d.score ?? d.band;
          if (scoreVal == null) return null;
          const hasRaw = d.raw != null && d.max != null;
          const hasTasks = isIELTS && sk === "writing" && (d.task1 != null || d.task2 != null);
          const hasDetail = hasRaw || hasTasks;
          return (
            <button key={sk} onClick={(ev) => { ev.currentTarget.blur(); if (hasDetail) setPopup(sk); }}
              style={{
                padding:"12px 4px", textAlign:"center",
                background:"transparent", border:"none",
                cursor: hasDetail ? "pointer" : "default",
              }}
            >
              <div style={{ fontSize:10, color:C.textSecondary, fontWeight:700, marginBottom:6, height:26, display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1.15 }}>{skillLabel(sk)}</div>
              <div style={{ fontSize:15, fontWeight:800, color:C.gray900, lineHeight:1 }}>
                {scoreVal.toFixed(1)}
              </div>
            </button>
          );
        })}
      </div>
      {popup && <RawPopup data={exam[popup]} skill={popup} isIELTS={isIELTS} combinedRW={combinedRW} onClose={() => setPopup(null)} />}
    </div>
  );
}

// ── Flat stroke nav icons (Threads/Instagram style) — currentColor theo active state ──
function NavIcon({ name, size = 21, bold = false }) {
  const common = { width:size, height:size, viewBox:"0 0 24 24", fill:"none", stroke:"currentColor", strokeWidth:bold ? 2.5 : 1.8, strokeLinecap:"round", strokeLinejoin:"round" };
  if (name === "daily") return (
    <svg {...common}>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M9 12h6M9 16h4" />
    </svg>
  );
  if (name === "progress") return (
    <svg {...common}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4.5" />
      <circle cx="12" cy="12" r="0.5" fill="currentColor" />
    </svg>
  );
  if (name === "materials") return (
    <svg {...common}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
  if (name === "report") return (
    <svg {...common}>
      <path d="M8 21h8M12 17v4" />
      <path d="M7 4h10v6a5 5 0 0 1-10 0V4z" />
      <path d="M7 6H4v1a3 3 0 0 0 3 3M17 6h3v1a3 3 0 0 1-3 3" />
    </svg>
  );
  // ── Section & link icons ──
  if (name === "attendance") return (
    <svg {...common}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
      <path d="M9 15.5l2 2 4-4" />
    </svg>
  );
  if (name === "vocab" || name === "writing") return (
    <svg {...common}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
  if (name === "book" || name === "reading") return (
    <svg {...common}>
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  );
  if (name === "video") return (
    <svg {...common}>
      <rect x="1" y="5" width="15" height="14" rx="2" />
      <path d="M23 7l-7 5 7 5V7z" />
    </svg>
  );
  if (name === "film") return (
    <svg {...common}>
      <rect x="2" y="2" width="20" height="20" rx="2.5" />
      <path d="M7 2v20M17 2v20M2 12h20M2 7h5M2 17h5M17 7h5M17 17h5" />
    </svg>
  );
  if (name === "folder") return (
    <svg {...common}>
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  );
  if (name === "play") return (
    <svg {...common}>
      <circle cx="12" cy="12" r="9" />
      <path d="M10 8.5l5.5 3.5-5.5 3.5z" />
    </svg>
  );
  if (name === "mic") return (
    <svg {...common}>
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8" />
    </svg>
  );
  if (name === "speaking") return (
    <svg {...common}>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
  if (name === "listening") return (
    <svg {...common}>
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
    </svg>
  );
  return null;
}

function TabBtn({ active, onClick, icon, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex:"1 1 0",
        minWidth:0,
        height:56,
        display:"flex",
        flexDirection:"column",
        alignItems:"center",
        justifyContent:"center",
        gap:3,
        padding:"4px 2px",
        border:"none",
        borderRadius:24,
        cursor:"pointer",
        background:"transparent",
        color:active ? C.ink : C.textMuted,
        fontWeight:700,
        fontSize:10,
        lineHeight:1,
        boxShadow:"none",
        transition:"color 0.18s ease",
        WebkitTapHighlightColor:"transparent"
      }}
    >
      <span style={{ lineHeight:1, width:24, height:24, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <NavIcon name={icon} size={23} bold={active} />
      </span>
      <span style={{ display:"block", width:"100%", textAlign:"center", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
        {label}
      </span>
    </button>
  );
}

// ── Current standing: where the child is now (level + latest score) ──
function CurrentStandingCard({ latestExamObj, baselineExam, isIELTS }) {
  if (!latestExamObj?.exam) return null;
  const exam = latestExamObj.exam;
  const latestOverall   = examOverallValue(exam);
  const baselineOverall = baselineExam ? examOverallValue(baselineExam) : null;
  const delta = (latestOverall != null && baselineOverall != null && exam !== baselineExam)
    ? latestOverall - baselineOverall : null;
  const levelText  = isIELTS ? (latestOverall != null ? `Band ${Number(latestOverall).toFixed(1)}` : "—") : (exam.cefr || "—");
  const levelLabel = isIELTS ? "Estimated IELTS Band" : "Estimated CEFR Level";
  const scoreText  = formatScoreValue(latestOverall, isIELTS);
  return (
    <Section title="Current Standing" titleEn={`Based on ${latestExamObj.label}`}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
        <div style={{ padding:"14px 12px", borderRadius:14, ...glassPanel("linear-gradient(135deg, rgba(254,205,211,0.55), rgba(254,242,242,0.32))", "rgba(255,255,255,0.7)") }}>
          <div style={{ fontSize:28, fontWeight:900, color:C.brand, lineHeight:1 }}>{levelText}</div>
          <div style={{ fontSize:10.5, color:C.brandDark, fontWeight:700, marginTop:6 }}>{levelLabel}</div>
        </div>
        <div style={{ padding:"14px 12px", borderRadius:14, ...glassPanel("linear-gradient(135deg, rgba(226,232,240,0.6), rgba(248,250,252,0.32))") }}>
          <div style={{ fontSize:24, fontWeight:900, color:C.text, lineHeight:1 }}>{scoreText}</div>
          <div style={{ fontSize:10.5, color:C.textSecondary, fontWeight:700, marginTop:6 }}>Latest assessment score</div>
          {delta != null && (
            <div style={{ marginTop:8, fontSize:11, fontWeight:800, color:getDeltaColor(delta) }}>{formatDelta(delta)} from Placement</div>
          )}
        </div>
      </div>
      <div style={{ marginTop:10, fontSize:11, color:C.textMuted, lineHeight:1.5 }}>
        This result is an internal estimate for learning guidance.
      </div>
    </Section>
  );
}

// ── Plain-language read of what the data shows ──
function ProgressSummaryCard({ summary, isIELTS }) {
  if (!summary) return null;
  const statusColor =
    summary.status === "Improving" ? C.success :
    summary.status === "Needs review" ? C.warning :
    summary.status === "Stable" ? C.info : C.textSecondary;
  const strongest = summary.strongestGrowth;
  const weakest = summary.weakestSkill;
  return (
    <Section title="Progress Summary" titleEn="What the assessment data shows">
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px", borderRadius:14, ...glassPanel("linear-gradient(135deg, rgba(226,232,240,0.6), rgba(248,250,252,0.32))"), marginBottom:10 }}>
        <div>
          <div style={{ fontSize:10.5, fontWeight:700, color:C.textMuted, textTransform:"uppercase", letterSpacing:.3 }}>Overall progress</div>
          <div style={{ fontSize:18, fontWeight:800, color:statusColor, marginTop:4 }}>{summary.status}</div>
        </div>
        {summary.overallDelta != null && (
          <div style={{ fontSize:18, fontWeight:800, color:getDeltaColor(summary.overallDelta) }}>{formatDelta(summary.overallDelta)}</div>
        )}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
        <div style={{ padding:"12px", borderRadius:14, ...glassPanel("linear-gradient(135deg, rgba(255,255,255,0.55), rgba(248,250,252,0.3))") }}>
          <div style={{ fontSize:10.5, fontWeight:700, color:C.textMuted, marginBottom:5 }}>Strongest growth</div>
          <div style={{ fontSize:14, fontWeight:800, color:C.text }}>
            {strongest
              ? (strongest.skills.length === 1 ? strongest.skills[0].label : `${strongest.skills.length} skills`)
              : "—"}
          </div>
          {strongest?.skills?.length > 1 && (
            <div style={{ fontSize:11, color:C.textSecondary, marginTop:4, lineHeight:1.4 }}>{strongest.labels.join(" · ")}</div>
          )}
          {strongest?.change != null && (
            <div style={{ fontSize:12, fontWeight:800, color:C.success, marginTop:4 }}>{formatDelta(strongest.change)}</div>
          )}
        </div>
        <div style={{ padding:"12px", borderRadius:14, ...glassPanel("linear-gradient(135deg, rgba(255,255,255,0.55), rgba(248,250,252,0.3))") }}>
          <div style={{ fontSize:10.5, fontWeight:700, color:C.textMuted, marginBottom:5 }}>Area to focus on</div>
          <div style={{ fontSize:14, fontWeight:800, color:C.text }}>{weakest ? weakest.label : "—"}</div>
          {weakest?.latest != null && (
            <div style={{ fontSize:12, fontWeight:800, color:C.warning, marginTop:4 }}>{formatScoreValue(weakest.latest, isIELTS)}</div>
          )}
        </div>
      </div>
    </Section>
  );
}

// ── Skill-by-skill assessment table (replaces the radar) ──
function SkillDeltaTable({ rows, isIELTS }) {
  if (!rows?.length) return null;
  const showPlacement = rows.some(r => r.placement != null);
  const showMidterm   = rows.some(r => r.midterm != null);
  const showFinal     = rows.some(r => r.final != null);
  const showChange    = rows.some(r => r.change != null);
  const cols = ["1.25fr", showPlacement ? ".9fr" : null, showMidterm ? ".9fr" : null, showFinal ? ".9fr" : null, showChange ? ".8fr" : null].filter(Boolean).join(" ");
  return (
    <Section title="Skill Progress" titleEn="Assessment comparison by skill">
      <div style={{ border:`1px solid ${C.borderSoft}`, borderRadius:14, overflow:"hidden" }}>
        <div style={{ display:"grid", gridTemplateColumns:cols, background:C.surfaceSoft, padding:"9px 10px", fontSize:10, fontWeight:700, color:C.textMuted }}>
          <div>Skill</div>
          {showPlacement && <div style={{ textAlign:"right" }}>Placement</div>}
          {showMidterm && <div style={{ textAlign:"right" }}>Midterm</div>}
          {showFinal && <div style={{ textAlign:"right" }}>Final</div>}
          {showChange && <div style={{ textAlign:"right" }}>Change</div>}
        </div>
        {rows.map((r, idx) => (
          <div key={r.key} style={{ display:"grid", gridTemplateColumns:cols, padding:"10px", fontSize:11.5, color:C.text, borderTop:idx ? `1px solid ${C.borderSoft}` : "none", alignItems:"center" }}>
            <div style={{ fontWeight:700, color:C.text }}>{r.label}</div>
            {showPlacement && <div style={{ textAlign:"right" }}>{r.placement != null ? Number(r.placement).toFixed(1) : "—"}</div>}
            {showMidterm && <div style={{ textAlign:"right" }}>{r.midterm != null ? Number(r.midterm).toFixed(1) : "—"}</div>}
            {showFinal && <div style={{ textAlign:"right" }}>{r.final != null ? Number(r.final).toFixed(1) : "—"}</div>}
            {showChange && <div style={{ textAlign:"right", fontWeight:800, color:getDeltaColor(r.change) }}>{formatDelta(r.change)}</div>}
          </div>
        ))}
      </div>
      {showChange && (
        <div style={{ marginTop:8, fontSize:10.5, color:C.textMuted, lineHeight:1.5 }}>
          Change compares the latest available assessment with the first comparable assessment.
        </div>
      )}
    </Section>
  );
}

// ── Cross-track skill table: same-track Midterm/Final only (no off-track placement) ──
function CrossTrackSkillTable({ rows, track, isCrossTrack }) {
  if (!rows?.length) return null;
  const showMidterm = rows.some(r => r.midterm != null);
  const showFinal   = rows.some(r => r.final != null);
  const showChange  = rows.some(r => r.change != null);
  const hasFinalComparison = showMidterm && showFinal;
  const cols = ["1.25fr", showMidterm ? ".9fr" : null, showFinal ? ".9fr" : null, showChange ? ".8fr" : null].filter(Boolean).join(" ");
  return (
    <Section
      title={hasFinalComparison ? `${track} Skill Progress` : `Current ${track} Skill Profile`}
      titleEn={hasFinalComparison ? "Midterm → Final comparison" : (showMidterm ? "Based on Midterm Check" : "Based on Final Assessment")}
    >
      <div style={{ border:`1px solid ${C.borderSoft}`, borderRadius:14, overflow:"hidden" }}>
        <div style={{ display:"grid", gridTemplateColumns:cols, background:C.surfaceSoft, padding:"9px 10px", fontSize:10, fontWeight:700, color:C.textMuted }}>
          <div>Skill</div>
          {showMidterm && <div style={{ textAlign:"right" }}>Midterm</div>}
          {showFinal && <div style={{ textAlign:"right" }}>Final</div>}
          {showChange && <div style={{ textAlign:"right" }}>Change</div>}
        </div>
        {rows.map((r, idx) => (
          <div key={r.key} style={{ display:"grid", gridTemplateColumns:cols, padding:"10px", fontSize:11.5, color:C.text, borderTop:idx ? `1px solid ${C.borderSoft}` : "none", alignItems:"center" }}>
            <div style={{ fontWeight:700, color:C.text }}>{r.label}</div>
            {showMidterm && <div style={{ textAlign:"right" }}>{r.midterm != null ? Number(r.midterm).toFixed(1) : "—"}</div>}
            {showFinal && <div style={{ textAlign:"right" }}>{r.final != null ? Number(r.final).toFixed(1) : "—"}</div>}
            {showChange && <div style={{ textAlign:"right", fontWeight:800, color:getDeltaColor(r.change) }}>{formatDelta(r.change)}</div>}
          </div>
        ))}
      </div>
      {isCrossTrack && (
        <div style={{ marginTop:8, fontSize:10.5, color:C.textMuted, lineHeight:1.5 }}>
          Initial placement is shown separately because it belongs to a different assessment track.
        </div>
      )}
    </Section>
  );
}

// ── Trust note about what the scores mean ──
function TrustLayer({ latestExamObj }) {
  return (
    <div style={{ margin:"4px 0 14px", padding:"10px 12px", borderRadius:12, background:C.surfaceSoft, border:`1px solid ${C.borderSoft}` }}>
      <div style={{ fontSize:10.5, color:C.textSecondary, lineHeight:1.5 }}>
        <strong>Assessment note:</strong> All scores are internal estimates for learning guidance, not official IELTS or Cambridge results.
      </div>
      {latestExamObj?.label && (
        <div style={{ fontSize:10.5, color:C.textMuted, marginTop:4 }}>Latest assessment source: {latestExamObj.label}</div>
      )}
    </div>
  );
}

// ── Language toggle (dùng chung Progress + Summary) — VI đứng trước vì là mặc định ──
function LangToggle({ showVi, setShowVi }) {
  const btn = (active) => ({
    padding:"5px 12px", borderRadius:20,
    border:`1px solid ${active ? C.ink : C.border}`,
    background:active ? C.ink : C.white,
    color:active ? C.white : C.textSecondary,
    fontSize:10, fontWeight:700, cursor:"pointer",
    fontFamily:"'Be Vietnam Pro',sans-serif",
  });
  return (
    <div style={{ display:"flex", gap:4 }}>
      <button onClick={() => setShowVi(true)}  style={btn(showVi)}>VI</button>
      <button onClick={() => setShowVi(false)} style={btn(!showVi)}>EN</button>
    </div>
  );
}

// ── Unified link card (Materials tab) — neutral glass, đồng bộ với panel bên Progress ──
function LinkRow({ url, icon, label, subtitle }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{
      display:"flex", alignItems:"center", gap:10,
      padding:"11px 12px", marginBottom:8, borderRadius:14,
      textDecoration:"none",
      ...glassPanel("linear-gradient(135deg, rgba(226,232,240,0.6), rgba(248,250,252,0.32))"),
    }}>
      <span style={{ width:34, height:34, borderRadius:10, background:"rgba(255,255,255,0.75)", border:`1px solid ${C.borderSoft}`, display:"flex", alignItems:"center", justifyContent:"center", color:C.textSecondary, flexShrink:0 }}><NavIcon name={icon} size={16} /></span>
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:12, fontWeight:700, color:C.text }}>{label}</div>
        <div style={{ fontSize:10, color:C.textMuted, marginTop:1 }}>{subtitle}</div>
      </div>
      <span style={{ marginLeft:"auto", color:C.gray400, flexShrink:0 }}>→</span>
    </a>
  );
}

// ── Page footer (pastel pink liquid glass, navy text) ──
function AppFooter() {
  // Footer = metadata im lặng: chữ nhỏ, màu lùi, hierarchy phẳng. Brand đã có ở header.
  const linkStyle = { fontSize:11, color:C.textSecondary, textDecoration:"none", fontWeight:600 };
  return (
    <footer style={{ margin:"auto 12px 0", padding:"16px 16px 96px", textAlign:"center", background:C.bg, borderTop:`1px solid ${C.border}` }}>
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
        {/* Một dòng liên hệ, ngăn cách | ; flexWrap để màn hẹp tự xuống dòng an toàn */}
        <div style={{ display:"flex", flexWrap:"wrap", justifyContent:"center", alignItems:"center", gap:"4px 8px" }}>
          <a href="https://www.ulead.vn" target="_blank" rel="noopener noreferrer" style={linkStyle}>ulead.vn</a>
          <span style={{ fontSize:10, color:C.gray400 }}>·</span>
          <a href="tel:0868412255" style={linkStyle}>0868 41 2255</a>
          <span style={{ fontSize:10, color:C.gray400 }}>·</span>
          <a href="mailto:info.spartacamp@gmail.com" style={linkStyle}>info.spartacamp@gmail.com</a>
        </div>
        <div style={{ fontSize:9.5, color:C.gray400, fontWeight:600 }}>© 2026 ULead Education</div>
      </div>
    </footer>
  );
}

// ── Header (brand hero card: gradient wine red, căn giữa, stats row) ──
function BannerHeader({ s, studentMeta, awards }) {
  const hasAwards = awards && awards.length > 0;
  const stats = [
    s.age != null && s.age !== "" && { value:s.age, label:"Age" },
    s.track && { value:s.track, label:"Track" },
    s.classGroup && { value:s.classGroup, label:"Class" },
  ].filter(Boolean);

  return (
    <div style={{
      margin:"10px 12px 0",
      borderRadius:30,
      padding:"24px 16px 18px",
      position:"relative",
      overflow:"hidden",
      background:"linear-gradient(170deg, #D8606B 0%, #C73D4B 55%, #AD2837 100%)",
      border:"1px solid rgba(255,255,255,0.18)",
      boxShadow:"0 6px 18px rgba(173,40,55,0.18), inset 0 1.5px 0 rgba(255,255,255,0.32), inset 0 -1px 0 rgba(0,0,0,0.14)",
      color:C.white,
      textAlign:"center"
    }}>
      {/* Sheen: vầng sáng nhẹ phía trên cho cảm giác chiều sâu */}
      <div aria-hidden="true" style={{ position:"absolute", inset:0, pointerEvents:"none", background:"radial-gradient(120% 80% at 50% -10%, rgba(255,255,255,0.20), rgba(255,255,255,0) 55%)" }} />

      <div style={{ position:"relative" }}>
        <div style={{ fontSize:10, fontWeight:700, letterSpacing:2.2, color:"rgba(255,255,255,0.78)", textTransform:"uppercase" }}>
          {s.camp}
        </div>

        <div style={{ fontSize:23, fontWeight:900, letterSpacing:-0.3, lineHeight:1.15, marginTop:8 }}>
          {s.name}
        </div>

        {hasAwards && (
          <div style={{ marginTop:10, display:"flex", flexWrap:"wrap", gap:6, justifyContent:"center" }}>
            {awards.map((aw,i) => (
              <span key={i} style={{
                padding:"4px 11px", borderRadius:999,
                background:"rgba(255,255,255,0.14)", border:"1px solid rgba(255,255,255,0.28)",
                fontSize:10.5, fontWeight:700, color:C.white
              }}>
                🏅 {aw.award} · W{aw.weekNum}
              </span>
            ))}
          </div>
        )}

        {stats.length > 0 && (
          <div style={{ display:"flex", marginTop:18 }}>
            {stats.map((st,i) => (
              <div key={i} style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:15, fontWeight:800, lineHeight:1 }}>{st.value}</div>
                <div style={{ fontSize:9, fontWeight:600, color:"rgba(255,255,255,0.62)", marginTop:4 }}>{st.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================
export default function App() {
  const [tab, setTab]       = useState("daily");
  const [sw, setSw]         = useState(0);
  const [showVi, setShowVi] = useState(true);
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [rechartsLib, setRechartsLib] = useState(null);

  useEffect(() => { loadRecharts().then(setRechartsLib).catch(() => {}); }, []);

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("code");

    if (!code) {
      setError("Vui lòng cung cấp mã tra cứu.\nVí dụ: ?code=NGOCANH2026");
      setLoading(false);
      return;
    }

    const cacheKey = `sparta-report:${code.trim().toUpperCase()}`;
    // Stale-while-revalidate trên localStorage (sống qua các lần đóng/mở Zalo webview):
    // có cache là hiện NGAY; nếu cache còn tươi (<TTL) thì khỏi gọi mạng,
    // cũ hơn thì âm thầm gọi API làm mới phía sau — phụ huynh không phải chờ.
    let hasCachedData = false;
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
      if (cached?.data?.student) {
        hasCachedData = true;
        setData(cached.data);
        if (cached.data.skills?.length) setSw(cached.data.skills.length - 1);
        setLoading(false);
        if (Date.now() - cached.ts < CLIENT_CACHE_TTL_MS) return;
      }
    } catch {
      try { localStorage.removeItem(cacheKey); } catch {}
    }

    // Fetch với timeout + 1 lần retry
    const fetchWithTimeout = (url, timeout = FETCH_TIMEOUT_MS) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeout);
      return fetch(url, { signal: controller.signal })
        .finally(() => clearTimeout(timer));
    };

    const loadData = async (isRetry = false) => {
      let retrying = false;
      try {
        const r = await fetchWithTimeout(`${API_URL}?code=${encodeURIComponent(code)}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();
        if (json.error) {
          if (!hasCachedData) setError("Vui lòng liên hệ trung tâm để được hỗ trợ.");
        } else {
          try { localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data: json })); } catch {}
          setData(json);
          // Refresh nền: giữ tuần đang xem, chỉ kẹp lại nếu vượt số tuần mới
          setSw(prev => json.skills?.length ? Math.min(hasCachedData ? prev : json.skills.length - 1, json.skills.length - 1) : prev);
        }
      } catch (err) {
        if (!isRetry && err.name !== "AbortError") {
          retrying = true;
          setTimeout(() => loadData(true), 800);
          return;
        }
        // Có dữ liệu cũ đang hiện thì lỗi mạng nền không cần làm phiền người xem
        if (!hasCachedData) setError("Lỗi kết nối. Vui lòng thử lại hoặc liên hệ trung tâm.");
      } finally {
        if (!retrying) setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) return <SkeletonScreen />;
  if (error)   return <ErrorScreen message={error} />;
  if (!data?.student) return <ErrorScreen message="Vui lòng liên hệ trung tâm để được hỗ trợ." />;

  const s        = data.student;
  const isIELTS  = s.track === "IELTS";
  const attendance = data.attendance || [];
  const vocab      = data.vocab      || [];
  const skills     = (data.skills || []).filter(isVisibleWeek);
  const exams      = data.exams      || {};
  const awards     = data.awards     || [];
  const links      = data.links      || [];
  const fin        = data.finalComments;

  const validVocab   = vocab.filter(v => v.counted && v.percent != null);
  const avgPct       = validVocab.length ? Math.round(validVocab.reduce((a,b) => a+b.percent, 0) / validVocab.length) : 0;
  const completedVocab = vocab.filter(v => v.counted).length;
  const totalVocabTests = vocab.length;
  const missedVocab    = vocab.filter(v => v.status === "absent").length;
  // Card 1-bài: màu theo mức điểm (không xanh cứng) + nhãn diễn giải cho phụ huynh
  const v1 = vocab.length === 1 ? vocab[0] : null;
  const v1Color = v1 ? (v1.percent >= 80 ? C.green : v1.percent >= 50 ? C.orange : C.red) : null;
  const v1Label = v1 ? (v1.percent >= 90 ? "Excellent" : v1.percent >= 80 ? "Very Good" : v1.percent >= 65 ? "Good" : v1.percent >= 50 ? "Fair" : "Needs Practice") : null;
  const selectedWeekIndex = skills.length ? Math.min(sw, skills.length - 1) : 0;
  const cur          = skills[selectedWeekIndex] || {};
  const commentKeys  = SKILL_KEYS.filter(sk => cur[`${sk}Comment`] || cur[`${sk}CommentVi`]);
  // Placement chỉ dùng làm mốc so sánh khi CÙNG hệ với chương trình học hiện tại.
  // (Không trộn Band IELTS 0–9 với điểm ESL 0–10.) Chuẩn hóa track phòng data có khoảng trắng/hoa thường.
  const hasPlacement       = hasExamData(exams.placement);
  const placementSameTrack = hasPlacement && normTrack(exams.placement?.track) === normTrack(s.track);
  const crossTrack         = hasPlacement && normTrack(exams.placement?.track) !== normTrack(s.track);

  // Cờ dữ liệu exam (dùng nhất quán hasExamData để tránh GAS trả {} bị tưởng có data).
  const hasPlacementData = hasExamData(exams.placement);
  const hasMidtermData   = hasExamData(exams.midterm);
  const hasFinalData     = hasExamData(exams.final);

  // Chỉ so sánh các bài thi khi cùng hệ/thang điểm.
  // Case đặc biệt: placement/midterm ESL nhưng final IELTS -> hiện từng bài riêng, không tính delta.
  const midFinalSameTrack = sameExamTrack(exams.midterm, exams.final);
  const hasMidFinalTrackMismatch = hasMidtermData && hasFinalData && !midFinalSameTrack;
  const placementComparableToMidterm = sameExamTrack(exams.placement, exams.midterm);
  const placementComparableToFinal   = sameExamTrack(exams.placement, exams.final);

  // Skill Progress chỉ hiện khi đủ điểm để so sánh / lập hồ sơ kỹ năng.
  const hasSameTrackSkillProgress  = !hasMidFinalTrackMismatch && placementSameTrack && hasPlacementData && (
    (hasMidtermData && placementComparableToMidterm) ||
    (hasFinalData && placementComparableToFinal)
  );
  const hasCrossTrackSkillProfile  = !hasMidFinalTrackMismatch && !placementSameTrack && (hasMidtermData || hasFinalData);

  // Progress Summary chỉ có ý nghĩa khi có ÍT NHẤT 2 mốc so sánh được và cùng track.
  // Nếu Midterm và Final khác track thì không hiện summary/delta để tránh trộn ESL /10 với IELTS band.
  const hasComparableProgress = !hasMidFinalTrackMismatch && (placementSameTrack
    ? hasPlacementData && ((hasMidtermData && placementComparableToMidterm) || (hasFinalData && placementComparableToFinal))
    : hasMidtermData && hasFinalData && midFinalSameTrack);

  // ── Exam-based, story-driven computed values (independent of weekly skills) ──
  const latestExamObj = getLatestExam(exams);
  const displayExamObj = getDisplayExam(exams);
  const skillDeltaRows = getSkillDeltaRows(exams, placementSameTrack);
  const crossTrackRows = getCrossTrackSkillRows(exams);
  const progressSummary = buildProgressSummary({ exams, latestExamObj, skillDeltaRows, placementSameTrack });
  // Câu chú thích đánh giá: nếu placement cùng hệ thì dùng câu theo hệ; nếu khác hệ
  // (vd thi IELTS đầu vào nhưng học ESL) thì dùng câu chung không nêu tên hệ.
  const assessmentNote = placementSameTrack
    ? (isIELTS
        ? "Band điểm là ước lượng nội bộ trong khuôn khổ Sparta Camp, mang tính tham khảo định hướng — không phải kết quả kỳ thi IELTS chính thức."
        : "Các mức CEFR, xếp loại và thang điểm là đánh giá nội bộ trong khuôn khổ Sparta Camp, mang tính tham khảo định hướng — không phải kết quả kỳ thi Cambridge chính thức.")
    : "Các kết quả đánh giá trong báo cáo là ước lượng nội bộ trong khuôn khổ Sparta Camp, dùng để định hướng học tập và theo dõi tiến bộ — không phải kết quả của kỳ thi chính thức.";
  // Nội dung popup "Thông tin đánh giá": câu chuyển hệ (nếu có) + câu chú thích chung
  const infoParagraphs = [
    ...(crossTrack ? [`Dựa trên kết quả đầu vào, trung tâm đã điều chỉnh lộ trình học từ ${exams.placement.track} sang ${s.track} để phù hợp hơn với nền tảng hiện tại của học sinh.`] : []),
    assessmentNote,
  ];
  const hwLinks      = links.filter(l => l.linkType === "homework");
  const videoLinks   = links.filter(l => l.linkType === "video");
  const finalVids    = links.filter(l => l.linkType === "final_video");
  const studentMeta  = [
    s.age ? `Age ${s.age}` : null,
    s.track || null,
    s.classGroup ? `Class ${s.classGroup}` : null,
  ].filter(Boolean).join(" · ");

  return (
    <div style={{ fontFamily:"'Be Vietnam Pro',sans-serif", background:C.bg, minHeight:"100vh", maxWidth:480, margin:"0 auto", display:"flex", flexDirection:"column" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Be+Vietnam+Pro:wght@400;600;700;800;900&display=swap'); *{box-sizing:border-box;} html, body, #root{margin:0; padding:0; width:100%; min-height:100%;} html{scrollbar-gutter:stable;} body{background:#F8FAFC;} button{font-family:'Be Vietnam Pro',sans-serif;} button, a{-webkit-tap-highlight-color:transparent;} button:focus{outline:none;}
        @keyframes tabIn{from{opacity:0;}to{opacity:1;}}
        .tab-content{animation:tabIn 0.18s ease-out;}
        button{transition:transform 0.12s ease;}
        button:active{transform:scale(0.96);}
        a:active{opacity:0.55;}
        @media (prefers-reduced-motion: reduce){.tab-content{animation:none;} button{transition:none;} button:active{transform:none;}}
        @supports not ((backdrop-filter: blur(2px)) or (-webkit-backdrop-filter: blur(2px))){.glassbar{background:rgba(255,255,255,0.94) !important;}} .recharts-wrapper, .recharts-wrapper svg, .recharts-surface{outline:none !important; -webkit-tap-highlight-color:transparent;} .recharts-wrapper *:focus{outline:none !important;}`}</style>

      {/* ── HEADER ─────────────────────────────────────────── */}
      <BannerHeader s={s} studentMeta={studentMeta} awards={awards} />

      {/* ── CONTENT ── key={tab}: remount khi đổi tab để chạy hiệu ứng tabIn ── */}
      <div key={tab} className="tab-content" style={{ padding:"18px 12px 0" }}>

        {/* ══ HẰNG NGÀY ══════════════════════════════════════ */}
        {tab === "daily" && (<>
          <Section title="Attendance">
            <CalendarGrid attendance={attendance} />
          </Section>

          <Section title="Daily Test">
            {totalVocabTests === 0 ? (
              /* Nhánh 1 — chưa có vocab test nào */
              <div style={{ padding:"22px 16px", textAlign:"center", fontSize:12, color:C.gray500, lineHeight:1.6 }}>
                Kết quả từ vựng sẽ hiển thị khi chương trình bắt đầu có bài kiểm tra từ vựng.
              </div>
            ) : vocab.length === 1 ? (
              /* 1 bài test → result card (chart 1 cột bị Recharts căn giữa, nhìn như trống) */
              <div style={{ padding:"18px 16px", borderRadius:14, background:C.gray50, border:`1px solid ${C.gray200}`, textAlign:"center" }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.gray500, marginBottom:8, letterSpacing:0.3 }}>Vocabulary Test · {vocab[0].date}</div>
                {vocab[0].status === "absent" ? (
                  <>
                    <div style={{ fontSize:26, fontWeight:900, color:C.gray500 }}>Absent</div>
                    <div style={{ fontSize:11, color:C.gray400, marginTop:4 }}>Không tính vào điểm trung bình</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize:34, fontWeight:900, color:v1Color, lineHeight:1 }}>{vocab[0].percent}%</div>
                    <div style={{ fontSize:12, fontWeight:700, color:v1Color, marginTop:4 }}>{v1Label}</div>
                    <div style={{ fontSize:11, color:C.gray400, marginTop:8 }}>{vocab[0].display} correct</div>
                  </>
                )}
              </div>
            ) : (
              /* ≥2 bài test → chart */
              <>
                <VocabChart RC={rechartsLib} vocab={vocab} />
                <div style={{ textAlign:"center", marginTop:6 }}>
                  {completedVocab > 0 ? (
                    null
                  ) : (
                    <span style={{ padding:"3px 14px", borderRadius:20, background:"rgba(226,232,240,0.55)", fontSize:11, fontWeight:700, color:C.gray500 }}>Chưa có bài hoàn thành</span>
                  )}
                  <div style={{ marginTop:6, fontSize:10, color:C.gray500, fontWeight:600 }}>
                    {avgPct > 0 && <span style={{ color:C.ink, fontWeight:800 }}>Average: {avgPct}%</span>}
                    {avgPct > 0 && " · "}
                    Completed: {completedVocab}/{totalVocabTests}{missedVocab > 0 ? ` · Missed: ${missedVocab}` : ""}
                  </div>
                </div>
              </>
            )}
          </Section>
        </>)}

        {/* ══ TIẾN BỘ ════════════════════════════════════════ */}
        {tab === "progress" && (<>

          {/* Placement Test */}
          {hasExamData(exams.placement) && (
            <Section title="Placement Test" titleEn="Initial assessment">
              <ExamCard exam={exams.placement} label="Placement" onInfo={() => setInfoOpen(true)} />
            </Section>
          )}

          {/* 3 · Cross-track note (detail in ⓘ popup) */}
          {crossTrack && (
            <div style={{ fontSize:11, color:C.textSecondary, fontWeight:600, margin:"-2px 4px 12px", display:"flex", alignItems:"center", gap:4 }}>
              Learning pathway: {exams.placement.track} → {s.track}
              <InfoDot onClick={() => setInfoOpen(true)} />
            </div>
          )}

          {/* 4 · Midterm / Final assessment card */}
          {hasMidFinalTrackMismatch ? (
            <>
              <Section title="Midterm Check" titleEn="Progress checkpoint">
                <ExamCard exam={exams.midterm} label="Midterm Check" onInfo={() => setInfoOpen(true)} />
              </Section>
              <Section title="Final Assessment" titleEn="Final learning outcome">
                <ExamCard exam={exams.final} label="Final Assessment" onInfo={() => setInfoOpen(true)} />
              </Section>
              <div style={{ fontSize:11, color:C.textSecondary, fontWeight:600, margin:"-2px 4px 12px", lineHeight:1.5 }}>
                Midterm and Final use different assessment tracks ({exams.midterm.track} → {exams.final.track}), so progress delta is not calculated.
              </div>
            </>
          ) : (
            displayExamObj && displayExamObj.key !== "placement" && (
              <Section title={displayExamObj.label} titleEn={displayExamObj.subtitle}>
                <ExamCard exam={displayExamObj.exam} label={displayExamObj.label} onInfo={() => setInfoOpen(true)} />
              </Section>
            )
          )}

          {/* 5 · Progress Summary — chỉ hiện khi có ≥2 mốc so sánh được */}
          {hasComparableProgress && (
            <ProgressSummaryCard summary={progressSummary} isIELTS={isIELTS} />
          )}

          {/* 6 · Skill comparison — same-track table, or cross-track (current-track only) */}
          {placementSameTrack ? (
            hasSameTrackSkillProgress && skillDeltaRows.length > 0 ? (
              <SkillDeltaTable rows={skillDeltaRows} isIELTS={isIELTS} />
            ) : null
          ) : (
            hasCrossTrackSkillProfile && crossTrackRows.length > 0 ? (
              <CrossTrackSkillTable rows={crossTrackRows} track={s.track} isCrossTrack={crossTrack} />
            ) : null
          )}

          {/* 8 · Teacher Feedback — only when weekly comments exist */}
          {skills.length > 0 && (<>
            <div style={{ position:"sticky", top:12, zIndex:5, padding:"6px 0 14px", marginBottom:4, background:"transparent" }}>
              <div className="glassbar" style={{ position:"relative", display:"flex", gap:4, padding:4, borderRadius:999, background:"linear-gradient(135deg, rgba(255,255,255,0.74), rgba(255,255,255,0.20))", backdropFilter:"blur(30px) saturate(190%)", WebkitBackdropFilter:"blur(30px) saturate(190%)", border:"1px solid rgba(255,255,255,0.65)", boxShadow:"0 8px 24px rgba(15,23,42,0.10), inset 0 1.5px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(15,23,42,0.05)", overflow:"hidden" }}>
                {skills.map((sk,i) => {
                  const active = selectedWeekIndex === i;
                  return (
                    <button key={i} onClick={() => setSw(i)} style={{ flex:1, minWidth:0, padding:"10px 4px", borderRadius:999, border:"none", background:"transparent", color:active ? C.ink : C.textMuted, fontWeight:active ? 800 : 700, fontSize:11, cursor:"pointer", boxShadow:"none", transition:"color 0.18s ease", lineHeight:1.2 }}>
                      <div>Week {sk.weekNum}</div>
                      <div style={{ fontSize:8.5, lineHeight:1.25, color:active ? C.textSecondary : C.textMuted, opacity:.9, marginTop:3, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{formatWeekRange(sk.weekRange)}</div>
                    </button>
                  );
                })}
                <div aria-hidden="true" style={{ position:"absolute", inset:0, pointerEvents:"none", borderRadius:999, background:"linear-gradient(115deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.25) 18%, rgba(255,255,255,0) 40%, rgba(255,255,255,0) 68%, rgba(255,255,255,0.5) 100%)", opacity:0.5 }} />
              </div>
            </div>

            <Section title="Teacher Feedback"
              headerRight={
                <LangToggle showVi={showVi} setShowVi={setShowVi} />
              }
            >
              {commentKeys.map(sk=>(
                <CommentBlock key={sk} label={cur[`${sk}Label`] ? `${SV[sk]} · ${cur[`${sk}Label`]}` : SV[sk]} icon="" comment={cur[`${sk}Comment`]} commentVi={cur[`${sk}CommentVi`]} showVi={showVi} />
              ))}
              {(cur.attitudeComment || cur.attitudeCommentVi) && (
                <CommentBlock label={showVi ? "Thái độ & Kỹ năng học tập" : "Attitude & Learning Skills"} icon="" comment={cur.attitudeComment} commentVi={cur.attitudeCommentVi} showVi={showVi} />
              )}
            </Section>
          </>)}

          {/* 9 · Trust note */}
          <TrustLayer latestExamObj={latestExamObj} />
        </>)}

        {/* ══ TÀI LIỆU ═══════════════════════════════════════ */}
        {tab === "links" && (<>
          {hwLinks.length > 0 && (
            <Section title="Homework" titleEn="Materials & Resources">
              {hwLinks.map((l,i)=><LinkRow key={i} url={l.url} icon="folder" label={l.label} subtitle={l.subtitle || "Homework & class materials"} />)}
            </Section>
          )}
          {videoLinks.length > 0 && (
            <Section title="Speaking Test Videos" titleEn="Watch your child's speaking test">
              {videoLinks.map((l,i)=><LinkRow key={i} url={l.url} icon="play" label={l.label} subtitle={l.subtitle || "Speaking test recording"} />)}
            </Section>
          )}
          {finalVids.length > 0 && (
            <Section title="Final Presentation">
              {finalVids.map((l,i)=><LinkRow key={i} url={l.url} icon="mic" label={l.label} subtitle={l.subtitle || "Final speaking video"} />)}
            </Section>
          )}
          {!links.length && <div style={{ textAlign:"center",padding:40,color:C.gray400,fontSize:13 }}>No materials uploaded yet.</div>}
        </>)}

        {/* ══ TỔNG KẾT ═══════════════════════════════════════ */}
        {tab === "summary" && (fin ? (
          <Section title="Final Report" titleEn="End of Camp Assessment" style={{ boxShadow:"0 6px 20px rgba(15,23,42,0.06)" }}
            headerRight={
              <LangToggle showVi={showVi} setShowVi={setShowVi} />
            }
          >
            {fin.overall && (
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:10, fontWeight:700, color:C.textSecondary, marginBottom:5, letterSpacing:0.5 }}>OVERALL</div>
                <p style={{ fontSize:12.5, color:C.text, lineHeight:1.65, margin:0, padding:"12px 13px", background:C.surfaceSoft, borderRadius:12, border:`1px solid ${C.borderSoft}` }}>
                  {showVi && fin.overallVi ? fin.overallVi : fin.overall}
                </p>
              </div>
            )}
            {[
              {key:"strengths",    keyVi:"strengthsVi",    label:"STRENGTHS"        },
              {key:"improvements", keyVi:"improvementsVi", label:"AREAS TO IMPROVE" },
              {key:"homePractice", keyVi:"homePracticeVi", label:"HOME PRACTICE"    },
              {key:"nextStep",     keyVi:"nextStepVi",     label:"NEXT STEP"        },
            ].filter(item=>fin[item.key]).map(item=>(
              <div key={item.key} style={{ marginBottom:12 }}>
                <div style={{ fontSize:10, fontWeight:700, color:C.textSecondary, marginBottom:5, letterSpacing:0.5 }}>{item.label}</div>
                <p style={{ fontSize:12.5, color:C.text, lineHeight:1.6, margin:0, padding:"12px 13px", background:C.surfaceSoft, borderRadius:12, border:`1px solid ${C.borderSoft}` }}>
                  {showVi && fin[item.keyVi] ? fin[item.keyVi] : fin[item.key]}
                </p>
              </div>
            ))}
            {fin.messageToParent && (
              <div style={{ marginTop:4, padding:"14px 15px", background:C.brandSoft, borderRadius:14, border:"1px solid #FECACA" }}>
                <div style={{ fontSize:10, fontWeight:700, color:C.brand, marginBottom:5, letterSpacing:0.5 }}>MESSAGE TO PARENT</div>
                <p style={{ fontSize:12.5, color:C.brandDark, lineHeight:1.65, margin:0 }}>
                  {showVi && fin.messageToParentVi ? fin.messageToParentVi : fin.messageToParent}
                </p>
              </div>
            )}
          </Section>
        ) : (
          <div style={{ textAlign:"center", padding:40, color:C.gray400, fontSize:13 }}>Final report not yet available.</div>
        ))}
      </div>

      {/* ── FOOTER ─────────────────────────────────────────── */}
      <AppFooter />

      {/* ── BOTTOM NAV ─────────────────────────────────────── */}
      <div className="glassbar" style={{ position:"fixed", bottom:16, left:"50%", transform:"translateX(-50%) translateZ(0)", width:"calc(100% - 28px)", maxWidth:452, height:70, background:"linear-gradient(135deg, rgba(255,255,255,0.72), rgba(255,255,255,0.16))", backdropFilter:"blur(32px) saturate(195%)", WebkitBackdropFilter:"blur(32px) saturate(195%)", border:"1px solid rgba(255,255,255,0.65)", borderRadius:30, display:"flex", alignItems:"center", padding:"6px 8px", boxShadow:"0 18px 44px rgba(15,23,42,0.18), inset 0 1.5px 0 rgba(255,255,255,0.9), inset 0 -1px 0 rgba(15,23,42,0.07)", zIndex:1000, willChange:"transform", overflow:"hidden" }}>
        <TabBtn active={tab==="daily"}    onClick={()=>setTab("daily")}    icon="daily"     label="Daily"     />
        <TabBtn active={tab==="progress"} onClick={()=>setTab("progress")} icon="progress"  label="Progress"  />
        <TabBtn active={tab==="links"}    onClick={()=>setTab("links")}    icon="materials" label="Materials" />
        <TabBtn active={tab==="summary"}  onClick={()=>setTab("summary")}  icon="report"    label="Report"    />
        {/* Specular sheen: vệt phản quang chéo trên mặt kính, độc lập với nền sau */}
        <div aria-hidden="true" style={{ position:"absolute", inset:0, pointerEvents:"none", borderRadius:30, background:"linear-gradient(115deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.25) 18%, rgba(255,255,255,0) 40%, rgba(255,255,255,0) 68%, rgba(255,255,255,0.5) 100%)", opacity:0.55 }} />
      </div>

      {infoOpen && <InfoModal paragraphs={infoParagraphs} onClose={() => setInfoOpen(false)} />}
    </div>
  );
}
